import { useMutation } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { DatasetDetailDto } from '@/Domain/api/Simulation/dto/Simulation.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  MAX_DATASET_MESSAGES,
  MAX_USER_MESSAGE_CHARS,
  parsePastedMessages,
} from '@/UI/Simulation/components/simulationUtils';

type Props = {
  datasetId: number;
  existingCount: number;
};

export const AddMessagesForm = ({ datasetId, existingCount }: Props) => {
  const { t } = useTranslation('simulation_page');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const remaining = Math.max(0, MAX_DATASET_MESSAGES - existingCount);
  const parsed = parsePastedMessages(draft);
  const parsedCount = parsed.length;
  const tooLongCount = parsed.filter((m) => m.length > MAX_USER_MESSAGE_CHARS).length;
  const exceedsRemaining = parsedCount > remaining;
  const exceedsCharLimit = tooLongCount > 0;
  const atCap = remaining === 0;

  const addMutation = useMutation<
    DatasetDetailDto,
    unknown,
    { datasetId: number; messages: { user_message: string }[] }
  >({ mutationKey: [QueryKey.simulation.addMessages] });

  const handleSubmit = () => {
    if (parsed.length === 0 || parsed.length > remaining || exceedsCharLimit) return;
    addMutation.mutate(
      { datasetId, messages: parsed.map((m) => ({ user_message: m })) },
      {
        onSuccess: () => {
          setDraft('');
          toast.success(t('toast_messages_added', { count: parsed.length }));
        },
        onError: () => toast.error(t('toast_add_messages_error')),
      },
    );
  };

  return (
    <div className="rounded-md border" data-testid="add-messages-form">
      <button
        type="button"
        data-testid="add-messages-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium"
      >
        <span className="flex items-center gap-1.5">
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          {t('add_messages_title')}
        </span>
        {atCap && (
          <span className="text-xs font-normal text-destructive">
            {t('add_messages_at_cap', { max: MAX_DATASET_MESSAGES })}
          </span>
        )}
      </button>
      {open && (
        <div className="space-y-2 border-t px-3 pb-3 pt-2">
          <textarea
            data-testid="add-messages-textarea"
            className={`h-28 w-full resize-y rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring ${
              exceedsRemaining || exceedsCharLimit
                ? 'border-destructive text-destructive focus:ring-destructive/30'
                : ''
            }`}
            placeholder={t('paste_placeholder')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={atCap}
          />
          <div
            className={`flex items-start justify-between gap-3 text-xs ${
              exceedsRemaining || exceedsCharLimit ? 'text-destructive' : 'text-muted-foreground'
            }`}
          >
            <span>
              {atCap
                ? t('add_messages_at_cap', { max: MAX_DATASET_MESSAGES })
                : exceedsCharLimit
                  ? t('paste_messages_too_long', {
                      count: tooLongCount,
                      max: MAX_USER_MESSAGE_CHARS,
                    })
                  : exceedsRemaining
                    ? t('add_messages_remaining_exceeded', { remaining })
                    : t('paste_separator_hint')}
            </span>
            {parsedCount > 0 && (
              <span data-testid="add-messages-count" className="shrink-0 tabular-nums">
                {t('messages_count', { count: parsedCount })}
              </span>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              data-testid="add-messages-submit"
              size="sm"
              onClick={handleSubmit}
              disabled={
                atCap ||
                parsedCount === 0 ||
                exceedsRemaining ||
                exceedsCharLimit ||
                addMutation.isPending
              }
            >
              {addMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="mr-1.5 h-3.5 w-3.5" />
              )}
              {t('add_messages_button')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
